// House instruments (docs/AUDIO_DESIGN.md §12), used only by house.js. The pump is the track's sidechain: one
// ConstantSource envelope scheduled on the beat grid of the kick sections ducks the chords and the bass through gains of
// their own, and the same grid runs the chord low-pass's section sweep. Chords: a drawbar-organ PeriodicWave, 2 detuned
// oscillators + a gain a note (cap with stealing), through a resonant low-pass (the sweep, plus a pluck on stabs), a dub
// echo and the pump. Bass (a saturated sine + triangle sub), kit (round kick, clap, soft snare, hats, shaker, rim, ride)
// and lead (a formant "vox" chop or a soft pluck) are persistent and gated with setTargetAtTime.
import { clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent as persist } from '../instruments.js';

// Level knobs (the harness zeroes them to measure one part alone).
export const HOUSE_LEVELS = { chords: 1, bass: 1, kit: 1, lead: 1 };

const lerp = (a, b, x) => a + (b - a) * x;
/** soft saturation y = tanh(kx) / tanh(k), 1025 points (odd harmonics only, so no DC) */
const tanhCurve = k => { const c = new Float32Array(1025); for (let i = 0; i < 1025; i++) c[i] = Math.tanh(k * (i / 512 - 1)) / Math.tanh(k); return c; };
const shaper = (ac, k) => { const w = ac.createWaveShaper(); w.curve = tanhCurve(k); return w; };

/* ---------------- pump: the sidechain + the chord LP's section sweep, on the track's beat grid ---------------- */
/** p = plan.house.pump: { chordDb, bassDb, atk, hold, rec (τ s) }; plan.house.chords gives the sweep's { open, build }.
 *  Every beat of an A or B bar (not a hats-only first bar of B: the kick's bars) ducks: env → 1 (τ atk), from +hold back
 *  to 0 (τ rec, about an 8th / 3). A part is a gain at 1 − (1 − 10^(−dB/20))·env. Beats are scheduled lazily, 2 bars past
 *  the latest call. The grid starts at the track start: t0 + 0.05 for a track built ahead (the engine's tStart = start −
 *  0.05), else t0; a drum event re-anchors it exactly (its time − e.t) when the engine moved the track after a stall. */
export function createPump(ac, plan, { registry, tag, t0, stepDur }) {
  const p = plan.house.pump, sw = plan.house.chords, beat = 4 * stepDur, bar = 16 * stepDur, on = [], secs = [];
  plan.sections.forEach((s, si) => {
    secs.push({ name: s.name, b0: on.length, n: s.bars, prev: si ? plan.sections[si - 1].name : null });
    for (let j = 0; j < s.bars; j++) on.push((s.name === 'A' || s.name === 'B') && !(j === 0 && s.layers.hatsFirst));
  });
  const env = ac.createConstantSource(), nodes = [env];
  env.offset.value = 0;
  const part = db => { const g = gain(ac, 1), d = gain(ac, Math.pow(10, -db / 20) - 1); env.connect(d); d.connect(g.gain); nodes.push(g, d); return g; };
  const chords = part(p.chordDb), bass = part(p.bassDb), V = persist(registry, tag, [env], nodes, t0), sweeps = [];
  let anchor = t0 - ac.currentTime > 0.004 ? t0 + 0.05 : t0, next = 0;
  function ensure(T) {
    for (const until = T + 2 * bar; next < on.length * 4 && anchor + next * beat <= until; next++) {
      const t = anchor + next * beat;
      if (!on[next >> 2] || t < ac.currentTime) continue;
      env.offset.setTargetAtTime(1, t, p.atk);
      env.offset.setTargetAtTime(0, t + p.hold, p.rec);
    }
  }
  // the chord LP per section, × the track's cutoff: intro .5 → .85 · A 1 · B sw.open (swept up over the last 2 bars of
  // an A) · break .45, sweeping up to sw.build over its 2nd half · outro → .3. [time, factor, τ (0 = set)]
  const timeline = () => secs.flatMap(({ name, b0, n, prev }) => {
    const t = anchor + b0 * bar, d = n * bar;
    if (name === 'intro') return [[t, 0.5, 0], [t, 0.85, d / 2.5]];
    if (name === 'A') return [[t, 1, 0.4 * bar]];
    if (name === 'B') return prev === 'A' ? [[t - 2 * bar, sw.open, 0.8 * bar]] : [[t, sw.open, 0.4 * bar]];
    if (name === 'break') return [[t, 0.45, 0.3 * bar], [t + d / 2, sw.build, d / 6]];
    return [[t, 0.3, d / 3]];
  }).sort((a, b) => a[0] - b[0]);
  function sweep(prm, base, T) {
    const ev = timeline(), past = ev.filter(e => e[0] < T), cur = past[past.length - 1];
    hold(prm, T);
    if (cur) prm.setTargetAtTime(base * cur[1], T, Math.max(0.05, cur[2]));
    for (const [t, f, tau] of ev) if (t >= T) { if (tau) prm.setTargetAtTime(base * f, t, tau); else prm.setValueAtTime(base * f, t); }
  }
  return {
    chords, bass, ensure,
    /** a drum event at T: start = T − e.t, the track start the engine used */
    lock(start, T) {
      if (Math.abs(start - anchor) < 0.002) return;
      anchor = start;
      hold(env.offset, T); env.offset.setTargetAtTime(0, T, p.rec);
      next = Math.max(0, Math.ceil((T - anchor) / beat - 0.1));
      for (const [prm, base] of sweeps) sweep(prm, base, T);
    },
    /** run the section sweep on prm (Hz = base × factor) */
    sweep(prm, base) { sweeps.push([prm, base]); sweep(prm, base, anchor); },
    dispose(t) { V.dispose(t); },
  };
}

/* ---------------- chords: drawbar organ (2 detuned PeriodicWave oscillators a note) → resonant LP → pump ---------------- */
// partials 1..16: rounder than a saw, hollow like an organ's drawbars (the 2nd, 4th, 6th and 8th a little up)
const ORGAN = [0, 1, 0.52, 0.36, 0.3, 0.14, 0.16, 0.06, 0.1, 0.035, 0.05, 0.02, 0.03, 0.015, 0.015, 0.012, 0.012];
const waves = new WeakMap();
const organ = ac => { if (!waves.has(ac)) waves.set(ac, ac.createPeriodicWave(new Float32Array(ORGAN.length), Float32Array.from(ORGAN))); return waves.get(ac); };

/** p = plan.house.chords: { detune, padDetune (± ¢), atk, rel, q, pluck, pluckTau, echo, open, build }; lp = the A cutoff.
 *  The note length sets the articulation: a stab (≤ 0.2 s) starts in 1.5 ms, falls to .55 (τ 140 ms) and its chord spikes
 *  the LP by lp·pluck·(.4 + .6v), back with τ pluckTau; a pad (≥ 0.5 s) swells in with τ atk and holds .75 (τ 1.6 s). A pad
 *  struck again where its own pitch's pad ends (within 80 ms) ties, so chords held over bars and common tones don't dip. */
export function createHouseChords(ac, out, send, p, lp, pump, { registry, tag, cap = 16, r = Math.random, t0, stepDur, log = null }) {
  // bus: LP (frequency = the section sweep, a ConstantSource, + the pluck on its own value; Q p.q) → pump → dry, reverb .3,
  // and a dub echo of 3 steps (a dotted 8th): HP 300 → LP 2.2 kHz, feedback .3, wet p.echo panned +.45
  const wave = organ(ac), busIn = gain(ac, 1), lpf = filter(ac, 'lowpass', 0, p.q), cut = ac.createConstantSource(), pg = pump.chords;
  const sendG = gain(ac, 0.3), dly = ac.createDelay(1), dHP = filter(ac, 'highpass', 300, 0.7), dLP = filter(ac, 'lowpass', 2200, 0.7);
  const fb = gain(ac, 0.3), wetG = gain(ac, p.echo), dPan = panner(ac, 0.45);
  dly.delayTime.value = Math.min(0.9, 3 * stepDur);
  cut.offset.value = 0.5 * lp; cut.connect(lpf.frequency);
  busIn.connect(lpf); lpf.connect(pg); pg.connect(out); pg.connect(sendG); sendG.connect(send);
  pg.connect(dly); dly.connect(dHP); dHP.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wetG); wetG.connect(dPan); dPan.connect(out);
  const bus = persist(registry, tag, [cut], [busIn, lpf, cut, sendG, dly, dHP, dLP, fb, wetG, dPan], t0);
  pump.sweep(cut.offset, lp);
  let voices = [], maxActive = 0, steals = 0, notes = 0;           // { v, midi, amp, A, pad, off, end (sounding end), rec }
  const cut10 = (x, t) => { registry.stop(x.v, t, 0.01); x.end = t; if (x.rec) x.rec[1] = t; };

  function note(t, midi, vel, dur, relTau = 0.08) {
    voices = voices.filter(x => x.end > t);
    const f = mtof(midi) * Math.pow(2, (r() * 4 - 2) / 1200), s = clamp((0.5 - dur) / 0.3, 0, 1);   // ±2 ¢ drift; 1 stab … 0 pad
    // amplitude 0.17·v^1.3 (stabs ×1.25), a little less up the keyboard ((262/f)^.2)
    const A = 0.17 * lerp(1, 1.25, s) * Math.pow(vel, 1.3) * clamp(Math.pow(262 / f, 0.2), 0.8, 1.15) * HOUSE_LEVELS.chords;
    const off = t + Math.max(0.05, dur), rel = Math.max(relTau, lerp(p.rel, 0.05, s));
    const k = voices.findIndex(x => x.midi === midi);
    if (k >= 0) {
      const y = voices[k];
      if (!s && y.pad && t < y.off + 0.08 && y.off > ac.currentTime + 0.02) {
        // a held pad struck again where it ends (a chord held over the bar line, a common tone): it ties, no re-attack;
        // the release is dropped and it drifts to the new level (τ 0.4 s)
        const g = y.amp.gain, o2 = Math.max(off, y.off), end = o2 + Math.max(0.3, rel * 6);
        g.cancelScheduledValues(y.off); g.setTargetAtTime(0.75 * A, y.off, 0.4); g.setTargetAtTime(0, o2, rel);
        for (const o of y.v.src) { try { o.stop(end); } catch { /* already stopped */ } }
        y.v.end = end; y.off = o2; y.end = o2 + rel * 4;
        if (y.rec) y.rec[1] = y.end;
        return;
      }
      cut10(voices.splice(k, 1)[0], t);                            // else the key is struck again: its old voice lets go
    } else if (voices.length >= cap) { cut10(voices.shift(), t); steals++; }   // else steal the oldest (10 ms)
    const o1 = osc(ac, 'sine', f), o2 = osc(ac, 'sine', f), amp = gain(ac, 0), det = lerp(p.padDetune, p.detune, s);
    o1.setPeriodicWave(wave); o2.setPeriodicWave(wave); o1.detune.value = -det; o2.detune.value = det;
    o1.connect(amp); o2.connect(amp); amp.connect(busIn);
    amp.gain.setValueAtTime(0, t);
    amp.gain.setTargetAtTime(A, t, lerp(p.atk, 0.0015, s));
    amp.gain.setTargetAtTime(A * lerp(0.75, 0.55, s), t + lerp(0.25, 0.01, s), lerp(1.6, 0.14, s));
    amp.gain.setTargetAtTime(0, off, rel);
    const end = off + Math.max(0.3, rel * 6);
    for (const o of [o1, o2]) { o.start(t); o.stop(end); }
    const v = registry.add({ src: [o1, o2], all: [o1, o2, amp], start: t, end, tag, out: amp });
    const x = { v, midi, amp, A, pad: !s, off, end: off + rel * 4, rec: log ? [t, off + rel * 4] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  return {
    note,
    /** all notes almost together (strum ≤ 8 ms), the top voice +6 %; a stab chord plucks the LP */
    chord(t, midis, vel, dur, strum = 0.003, relTau = 0.08) {
      pump.ensure(t + dur);
      const n = midis.length, s = clamp((0.5 - dur) / 0.3, 0, 1);
      hold(lpf.frequency, t);
      lpf.frequency.setTargetAtTime(lp * p.pluck * (0.4 + 0.6 * vel) * s, t, 0.003);
      lpf.frequency.setTargetAtTime(0, t + 0.008, p.pluckTau);
      midis.forEach((m, k) => { const dt = n > 1 ? (k * strum) / (n - 1) : 0; note(t + dt, m, k === n - 1 ? Math.min(1, vel * 1.06) : vel, dur - dt, relTau); });
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); pump.dispose(t); },
  };
}

/* ---------------- bass: a round sine + triangle sub through a soft tanh (mono, persistent) → pump ---------------- */
/** p = plan.house.bass: { tri (triangle level), drive (tanh k), lp (Hz) }. Sine + triangle → tanh → LP p.lp (Q .7), spiked
 *  ×2 on each note (τ 80 ms: a soft thump, and the 3rd/5th harmonics carry it on phones); amplitude .3v (attack τ 4 ms,
 *  to .8 over τ .3 s, release τ 28 ms, so the off-beats bounce); slides glide τ 25 ms. */
export function createHouseBass(ac, out, p, pump, { registry, tag, t0 }) {
  const sine = osc(ac, 'sine', 55), tri = osc(ac, 'triangle', 55), triG = gain(ac, p.tri), drive = gain(ac, 1 / (1 + p.tri));
  const sh = shaper(ac, p.drive), lpf = filter(ac, 'lowpass', p.lp, 0.7), amp = gain(ac, 0), pg = pump.bass;
  sine.connect(drive); tri.connect(triG); triG.connect(drive); drive.connect(sh); sh.connect(lpf); lpf.connect(amp); amp.connect(pg); pg.connect(out);
  const P = persist(registry, tag, [sine, tri], [sine, tri, triG, drive, sh, lpf, amp], t0);
  return {
    note(t, midi, vel, dur, slide = false) {
      pump.ensure(t);
      const f = mtof(midi);
      for (const prm of [sine.frequency, tri.frequency, lpf.frequency, amp.gain]) hold(prm, t);
      for (const o of [sine, tri]) { if (slide) o.frequency.setTargetAtTime(f, t, 0.025); else o.frequency.setValueAtTime(f, t); }
      lpf.frequency.setTargetAtTime(2 * p.lp, t, 0.002); lpf.frequency.setTargetAtTime(p.lp, t + 0.006, 0.08);
      const A = 0.3 * vel * HOUSE_LEVELS.bass;
      amp.gain.setTargetAtTime(A, t, 0.004); amp.gain.setTargetAtTime(0.8 * A, t + 0.03, 0.3);
      amp.gain.setTargetAtTime(0, t + Math.max(0.05, dur), 0.028);
    },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- kit: round deep-house kick, clap, soft snare, hats, shaker, rim, ride (the block lane) ---------------- */
/** kit = plan.drums.kit: { kickF0, kickF1, kickSweep, kickDecay, clapF, clapTail, snareDecay, hatHP, hatClosed, openDecay,
 *  shakerDecay, rideDecay, lp }. One noise source feeds every noise voice; bus LP kit.lp. Each hit also keeps the pump's
 *  grid (the event's time − e.t is the track start). */
export function createHouseKit(ac, out, send, kit, pump, { registry, tag, t0 }) {
  const L = LEVELS.kit * HOUSE_LEVELS.kit, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  /** a gated voice: amp → pan (null: straight to the bus) → bus, plus a reverb send */
  const voice = (pan, rev = 0) => {
    const a = N(gain(ac, 0)), pn = pan == null ? a : N(panner(ac, pan));
    if (pn !== a) a.connect(pn);
    pn.connect(bus);
    if (rev) { const s = N(gain(ac, rev)); pn.connect(s); s.connect(send); }
    return a;
  };
  // kick: sine kickF0 (115-140 Hz) → kickF1 (47-54 Hz), τ kickSweep, through a soft tanh (k 1.6: a little 3rd harmonic
  // for phones); a dull thump (noise → LP 700 Hz, τ 6 ms) instead of a 909 click
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kSh = N(shaper(ac, 1.6)), kAmp = voice(null);
  kOsc.connect(kSh); kSh.connect(kAmp);
  const kLP = N(filter(ac, 'lowpass', 700, 0.7)), kThump = voice(null);
  src.connect(kLP); kLP.connect(kThump);
  // clap: noise → BP clapF (Q .9) → HP 600, three bursts 9 ms apart then a tail τ clapTail; pan −.08, reverb .35
  const cBP = N(filter(ac, 'bandpass', kit.clapF, 0.9)), cHP = N(filter(ac, 'highpass', 600, 0.7)), cAmp = voice(-0.08, 0.35);
  src.connect(cBP); cBP.connect(cHP); cHP.connect(cAmp);
  // soft snare (layered under the clap, ghosts in B): triangle 200 → 175 Hz + noise BP 1.9 kHz; pan −.04, reverb .22
  const sOsc = N(osc(ac, 'triangle', 175)), sTone = voice(-0.04, 0.22);
  sOsc.connect(sTone);
  const sBP = N(filter(ac, 'bandpass', 1900, 0.7)), sNoise = voice(-0.04, 0.22);
  src.connect(sBP); sBP.connect(sNoise);
  // hats: noise → HP hatHP; closed τ hatClosed, open τ openDecay on one gain (a closed hit chokes an open one); pan +.22
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hAmp = voice(0.22, 0.08);
  src.connect(hHP); hHP.connect(hAmp);
  // shaker: noise → HP 5 kHz → BP 8 kHz, attack τ 5 ms, τ shakerDecay; pan −.3
  const xHP = N(filter(ac, 'highpass', 5000, 0.7)), xBP = N(filter(ac, 'bandpass', 8000, 0.8)), xAmp = voice(-0.3);
  src.connect(xHP); xHP.connect(xBP); xBP.connect(xAmp);
  // rim tok: sine 560 (.6) + triangle 1480 (.4) → HP 350, τ 10 ms; pan −.18, reverb .25
  const r1 = N(osc(ac, 'sine', 560)), r2 = N(osc(ac, 'triangle', 1480)), rg2 = N(gain(ac, 0.66)), rHP = N(filter(ac, 'highpass', 350, 0.7)), rAmp = voice(-0.18, 0.25);
  r1.connect(rHP); r2.connect(rg2); rg2.connect(rHP); rHP.connect(rAmp);
  // ride (the block lane): noise → BP 5.2 kHz (Q 1.4) + two squares 540 / 800 Hz (.2) → HP 4.5 kHz, τ rideDecay; pan +.35, reverb .15
  const dBP = N(filter(ac, 'bandpass', 5200, 1.4)), q1 = N(osc(ac, 'square', 540)), q2 = N(osc(ac, 'square', 800)), qG = N(gain(ac, 0.2));
  const qHP = N(filter(ac, 'highpass', 4500, 0.7)), dAmp = voice(0.35, 0.15);
  src.connect(dBP); dBP.connect(dAmp); q1.connect(qG); q2.connect(qG); qG.connect(qHP); qHP.connect(dAmp);
  const P = persist(registry, tag, [src, kOsc, sOsc, r1, r2, q1, q2], nodes, t0);
  const hit = (prm, peak, t, atk, rel, tau) => { prm.setTargetAtTime(peak, t, atk); prm.setTargetAtTime(0, t + rel, tau); };
  const sync = (t, e) => { if (e) pump.lock(t - e.t, t); pump.ensure(t); };
  return {
    kick(t, v, e) {
      sync(t, e);
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, kit.kickSweep);
      hit(kAmp.gain, 0.32 * v * L, t, 0.002, 0.008, kit.kickDecay);
      hit(kThump.gain, 0.5 * v * L, t, 0.0008, 0.003, 0.006);
    },
    clap(t, v, e) {
      sync(t, e);
      const A = 1.3 * v * L;
      for (const d of [0, 0.009, 0.019]) hit(cAmp.gain, A * (d ? 0.8 : 1), t + d, 0.0004, 0.002, 0.003);
      hit(cAmp.gain, 0.7 * A, t + 0.028, 0.0006, 0.002, kit.clapTail);
    },
    snare(t, v, ghost = false, e) {
      sync(t, e);
      sOsc.frequency.setValueAtTime(200, t); sOsc.frequency.setTargetAtTime(175, t, 0.03);
      hit(sTone.gain, 0.16 * v * L, t, 0.001, 0.004, 0.045);
      hit(sNoise.gain, 0.26 * v * L, t, 0.001, 0.004, ghost ? 0.03 : kit.snareDecay);
    },
    hat(t, v, open = false, e) { sync(t, e); hit(hAmp.gain, 0.15 * v * L, t, 0.0008, 0.003, open ? kit.openDecay : kit.hatClosed); },
    shaker(t, v, e) { sync(t, e); hit(xAmp.gain, 0.12 * v * L, t, 0.005, 0.01, kit.shakerDecay); },
    rim(t, v, e) { sync(t, e); hit(rAmp.gain, 0.2 * v * L, t, 0.0008, 0.002, 0.01); },
    block(t, v, e) { sync(t, e); hit(dAmp.gain, 0.09 * v * L, t, 0.001, 0.004, kit.rideDecay); },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- lead: 'vox' (a formant vocal chop) or 'pluck', mono, dotted-8th echo ---------------- */
const VOWELS = [[800, 1150], [560, 880], [600, 1700], [700, 1250]];     // ah · oh · eh · a: (F1, F2) Hz
/** lead = plan.lead ({ voice, pan }), p = plan.house.lead ({ vib (¢), glide (τ s) }); stepDur sets the echo (3 sixteenths). */
export function createHouseLead(ac, out, send, lead, p, { registry, tag, stepDur, t0, r = Math.random }) {
  const nodes = [], src = [], N = x => (nodes.push(x), x);
  const amp = N(gain(ac, 0)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.38));
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 2200, 0.5)), fb = N(gain(ac, 0.3)), wet = N(gain(ac, 0.3));
  dly.delayTime.value = Math.min(1.9, 3 * stepDur);
  amp.connect(pan); amp.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  pan.connect(out); pan.connect(sendG); sendG.connect(send);
  const L = HOUSE_LEVELS.lead;
  let play;
  if (lead.voice === 'vox') {
    // 2 saws (+7 ¢) → formants F1 (BP Q 5) + F2 (BP Q 8, .55) → LP 3 kHz. Each chop opens from "oo" (330 / 850 Hz) to its
    // vowel (τ 35 / 45 ms); amplitude 1.2v (attack τ 10 ms, to .8 over τ .3 s, release τ 70 ms); vibrato 5.2 Hz ±p.vib ¢
    // from 180 ms; a legato step of ≤ 5 semitones glides (τ p.glide) and keeps its vowel
    const s1 = N(osc(ac, 'sawtooth', 440)), s2 = N(osc(ac, 'sawtooth', 440)), vib = N(osc(ac, 'sine', 5.2)), vibG = N(gain(ac, 0));
    const f1 = N(filter(ac, 'bandpass', 700, 5)), f2 = N(filter(ac, 'bandpass', 1200, 8)), g2 = N(gain(ac, 0.55)), lp = N(filter(ac, 'lowpass', 3000, 0.5));
    s2.detune.value = 7;
    for (const s of [s1, s2]) { s.connect(f1); s.connect(f2); }
    vib.connect(vibG); vibG.connect(s1.detune); vibG.connect(s2.detune);
    f1.connect(lp); f2.connect(g2); g2.connect(lp); lp.connect(amp);
    src.push(s1, s2, vib);
    let lastF = 0;
    play = (t, f, vel, dur, legato) => {
      const glide = legato && Math.abs(12 * Math.log2(f / (lastF || f))) <= 5, [F1, F2] = VOWELS[Math.floor(r() * VOWELS.length)];
      lastF = f;
      for (const prm of [s1.frequency, s2.frequency, f1.frequency, f2.frequency, amp.gain, vibG.gain]) hold(prm, t);
      for (const o of [s1, s2]) { if (glide) o.frequency.setTargetAtTime(f, t, p.glide); else o.frequency.setValueAtTime(f, t); }
      if (!glide) { f1.frequency.setTargetAtTime(330, t, 0.004); f2.frequency.setTargetAtTime(850, t, 0.004); }
      f1.frequency.setTargetAtTime(F1, t + 0.015, 0.035); f2.frequency.setTargetAtTime(F2, t + 0.015, 0.045);
      const A = 1.2 * vel * L;
      amp.gain.setTargetAtTime(A, t, glide ? 0.02 : 0.01); amp.gain.setTargetAtTime(0.8 * A, t + 0.06, 0.3);
      amp.gain.setTargetAtTime(0, t + dur, 0.07);
      vibG.gain.setTargetAtTime(0, t, 0.02);
      if (dur > 0.3) vibG.gain.setTargetAtTime(p.vib, t + 0.18, 0.12);
    };
  } else {
    // pluck: triangle + sine 2f (.3) → LP opening to max(2.2k, 3f), closing to 1.1f (τ 110 ms); amplitude .65v (attack
    // τ 1.5 ms, decay τ .32 s, release τ 60 ms): a soft marimba-like pluck
    const tri = N(osc(ac, 'triangle', 440)), s2 = N(osc(ac, 'sine', 880)), g2 = N(gain(ac, 0.3)), lp = N(filter(ac, 'lowpass', 1500, 0.8));
    tri.connect(lp); s2.connect(g2); g2.connect(lp); lp.connect(amp);
    src.push(tri, s2);
    play = (t, f, vel, dur) => {
      for (const prm of [tri.frequency, s2.frequency, lp.frequency, amp.gain]) hold(prm, t);
      tri.frequency.setValueAtTime(f, t); s2.frequency.setValueAtTime(2 * f, t);
      lp.frequency.setTargetAtTime(Math.max(2200, 3 * f), t, 0.002); lp.frequency.setTargetAtTime(1.1 * f, t + 0.005, 0.11);
      const A = 0.65 * vel * L;
      amp.gain.setTargetAtTime(A, t, 0.0015); amp.gain.setTargetAtTime(0, t + 0.004, 0.32);
      amp.gain.setTargetAtTime(0, t + dur, 0.06);
    };
  }
  const P = persist(registry, tag, src, nodes, t0);
  return {
    note(t, midi, vel, dur, glide = false) { play(t, mtof(midi), vel, Math.max(0.06, dur), glide); },
    dispose(t) { P.dispose(t); },
  };
}
